import { BANNED_WORD_MESSAGE, bannedWordOr } from "@/lib/bannedWords";
import { useState } from "react";
import { z } from "zod";
import { Loader2, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import AgeOnboardingForm from "@/components/onboarding/AgeOnboardingForm";
import { useSecuritySettings } from "@/hooks/useSecuritySettings";
import AvatarCropDialog from "@/components/AvatarCropDialog";
import {
  avatarPathFromUrl,
  getAvatarMaxMb,
  removeObsoleteAvatars,
  uploadCroppedAvatar,
  validateAvatarSource,
} from "@/lib/avatarUpload";
import { getUsernameLengths, isValidUsername, usernameRuleMessage } from "@/lib/usernameRules";

/** Identité minimale exigée avant d'utiliser Tama. */
const identitySchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, { message: "Le nom doit contenir au moins 2 caractères" })
    .max(60, { message: "Le nom ne peut pas dépasser 60 caractères" }),
  username: z
    .string()
    .trim(),
});


interface OnboardingWizardProps {
  /** L'étape d'âge est-elle encore nécessaire ? */
  needsAge: boolean;
  /** L'identité (nom + pseudo) est-elle encore à compléter ? */
  needsIdentity: boolean;
  /** Appelé quand l'assistant est terminé (rafraîchit l'état côté appelant). */
  onCompleted: () => void;
}

/**
 * Assistant de finalisation de compte affiché après la création du compte :
 * 1) déclaration d'âge (obligatoire, vérifiée côté serveur),
 * 2) nom et pseudo (obligatoires),
 * 3) bio et photo (facultatives).
 */
export const OnboardingWizard = ({
  needsAge,
  needsIdentity,
  onCompleted,
}: OnboardingWizardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: securitySettings } = useSecuritySettings();
  const { min: usernameMin, max: usernameMax } = getUsernameLengths(securitySettings?.auth_security);


  const [step, setStep] = useState<"age" | "identity" | "extras">(
    needsAge ? "age" : needsIdentity ? "identity" : "extras"
  );
  const [fullName, setFullName] = useState(
    (user?.user_metadata?.full_name as string) || ""
  );
  const [username, setUsername] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  const showError = (description: string) =>
    toast({ variant: "destructive", title: "Erreur", description });

  const handleIdentitySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) return;

    const parsed = identitySchema.safeParse({ fullName, username });
    if (!parsed.success) {
      showError(parsed.error.errors[0].message);
      return;
    }
    if (!isValidUsername(parsed.data.username, usernameMin, usernameMax)) {
      showError(usernameRuleMessage(usernameMin, usernameMax));
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: parsed.data.fullName,
          username: parsed.data.username,
        })
        .eq("id", user.id);

      if (error) {
        // 23505 = violation d'unicité sur le pseudo.
        showError(
          error.code === "23505"
            ? "Ce pseudo est déjà utilisé. Choisissez-en un autre."
            : error.message?.includes("Ce pseudo est réservé.")
            ? "Ce pseudo est réservé."
            : error.message?.includes("mot interdit")
            ? BANNED_WORD_MESSAGE
            : error.message?.includes("Pseudo invalide")
            ? usernameRuleMessage(usernameMin, usernameMax)
            : "Enregistrement impossible. Merci de réessayer."
        );
        return;
      }

      setStep("extras");
    } finally {
      setSaving(false);
    }
  };

  /** Choix du fichier : validation puis ouverture du recadrage carré. */
  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user) return;

    const invalid = validateAvatarSource(
      file,
      getAvatarMaxMb(securitySettings?.auth_security?.avatar_max_file_size_mb)
    );
    if (invalid) {
      showError(invalid);
      return;
    }
    setAvatarFile(file);
    setCropOpen(true);
  };

  /** Envoi du carré 512 × 512 recadré, puis aperçu. */
  const handleCroppedAvatar = async (blob: Blob) => {
    if (!user) return;
    setSaving(true);
    try {
      const { publicUrl } = await uploadCroppedAvatar(user.id, blob);
      setAvatarUrl(publicUrl);
      setCropOpen(false);
      setAvatarFile(null);
    } catch (error) {
      console.error("Avatar upload failed", error);
      showError("L'envoi de la photo a échoué. Merci de réessayer.");
    } finally {
      setSaving(false);
    }
  };

  const handleExtrasSubmit = async (skip: boolean) => {
    if (!user) return;
    if (skip && !avatarUrl && !bio.trim()) {
      onCompleted();
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          bio: bio.trim() || null,
          ...(avatarUrl ? { avatar_url: avatarUrl } : {}),
        })
        .eq("id", user.id);

      if (error) {
        showError(bannedWordOr(error, "Enregistrement impossible. Merci de réessayer."));
        return;
      }

      // Ne garder que la photo retenue (essais précédents supprimés).
      if (avatarUrl) {
        await removeObsoleteAvatars(user.id, avatarPathFromUrl(avatarUrl));
      }
      onCompleted();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="tama-surface-card w-full max-w-md min-w-0">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold tracking-tight text-center">
          {step === "age"
            ? "Vérification de l'âge"
            : step === "identity"
            ? "Votre profil"
            : "Personnalisez votre profil"}
        </CardTitle>
        <CardDescription className="text-center">
          {step === "age"
            ? "Renseignez votre date de naissance pour accéder à Tama"
            : step === "identity"
            ? "Choisissez le nom et le pseudo visibles par la communauté"
            : "Ajoutez une photo et une courte présentation (facultatif)"}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {step === "age" && (
          <AgeOnboardingForm
            submitLabel="Continuer"
            onError={showError}
            onSuccess={() => setStep(needsIdentity ? "identity" : "extras")}
          />
        )}

        {step === "identity" && (
          <form onSubmit={handleIdentitySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="onboardFullName">
                Nom complet <span className="text-destructive">*</span>
              </Label>
              <Input
                id="onboardFullName"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Votre nom"
                required
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="onboardUsername">
                Pseudo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="onboardUsername"
                value={username}
                onChange={(event) => setUsername(event.target.value.toLowerCase())}
                placeholder="pseudo_tama"
                required
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                {usernameRuleMessage(usernameMin, usernameMax)}
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Continuer"
              )}
            </Button>
          </form>
        )}

        {step === "extras" && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarUrl ?? undefined} alt="Photo de profil" />
                <AvatarFallback>{fullName.slice(0, 2).toUpperCase() || "TA"}</AvatarFallback>
              </Avatar>
              <Label
                htmlFor="onboardAvatar"
                className="inline-flex cursor-pointer items-center gap-2 text-sm text-primary hover:underline"
              >
                <Camera className="h-4 w-4" />
                Ajouter une photo
              </Label>
              <input
                id="onboardAvatar"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
                disabled={saving}
              />
              <AvatarCropDialog
                open={cropOpen}
                onOpenChange={(next) => {
                  setCropOpen(next);
                  if (!next) setAvatarFile(null);
                }}
                file={avatarFile}
                onCropped={handleCroppedAvatar}
                saving={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="onboardBio">Bio</Label>
              <Textarea
                id="onboardBio"
                className="text-base"
                value={bio}
                maxLength={160}
                onChange={(event) => setBio(event.target.value)}
                placeholder="Présentez-vous en quelques mots"
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Button
                type="button"
                className="w-full"
                onClick={() => handleExtrasSubmit(false)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Terminer"
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => handleExtrasSubmit(true)}
                disabled={saving}
              >
                Pas tout de suite
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnboardingWizard;
